const trackedKeys = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'source',
  'campaign',
  'landing_variant',
  'post',
];

const scrollThresholds = [25, 50, 75, 90] as const;
const scrollEventNames = {
  25: 'Scroll25',
  50: 'Scroll50',
  75: 'Scroll75',
  90: 'Scroll90',
} as const;
const allowedPaymentFailureCategories = ['validation', 'declined', 'authentication', 'network', 'unknown'] as const;

export type ScrollDepth = (typeof scrollThresholds)[number];
export type CtaPosition = 'sticky' | 'final';
export type PaymentMethod = 'card' | 'paypal' | 'apple_pay' | 'google_pay' | 'link';
export type PaymentFailureCategory = (typeof allowedPaymentFailureCategories)[number];
export type PaymentPresentationMode = 'modal';
export type PlausibleEventName =
  | 'Scroll25'
  | 'Scroll50'
  | 'Scroll75'
  | 'Scroll90'
  | 'CtaClicked'
  | 'CheckoutViewed'
  | 'CheckoutStarted'
  | 'CheckoutContactCompleted'
  | 'PaymentMethodSelected'
  | 'PaymentInitiated'
  | 'PaymentFailed'
  | 'PaymentCanceled';

type PlausibleProps = {
  position?: CtaPosition;
  method?: PaymentMethod;
  category?: PaymentFailureCategory;
  presentation_mode?: PaymentPresentationMode;
};

const sentOnce = new Set<PlausibleEventName>();
const sentScrollDepths = new Set<ScrollDepth>();

export function captureTrackingFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const current = getTracking();
  for (const key of trackedKeys) {
    const value = params.get(key);
    if (value) current[key] = value.slice(0, 120);
  }
  sessionStorage.setItem('funnel_tracking', JSON.stringify(current));
  if (!sessionStorage.getItem('funnel_entry_url')) {
    sessionStorage.setItem('funnel_entry_url', sanitizeCurrentEntryUrl());
  }
  if (!sessionStorage.getItem('funnel_initial_referrer') && document.referrer) {
    sessionStorage.setItem('funnel_initial_referrer', document.referrer);
  }
}

export function getTracking(extra?: Record<string, string>) {
  const raw = sessionStorage.getItem('funnel_tracking');
  const parsed = raw ? safeParse(raw) : {};
  return { ...parsed, ...(extra ?? {}) };
}

export function track(eventName: PlausibleEventName, props?: PlausibleProps): boolean {
  try {
    const plausible = window.plausible;
    if (typeof plausible !== 'function') return false;
    if (props && Object.keys(props).length > 0) {
      plausible(eventName, { props });
    } else {
      plausible(eventName);
    }
    return true;
  } catch {
    return false;
  }
}

export function installPlausible() {
  window.plausible =
    window.plausible ||
    function plausible(...args: unknown[]) {
      (window.plausible!.q = window.plausible!.q || []).push(args);
    };
  window.plausible.init =
    window.plausible.init ||
    function init(options?: Record<string, unknown>) {
      window.plausible!.o = options || {};
    };
  window.plausible.init();
}

export function trackScrollDepth(depth: ScrollDepth): boolean {
  if (sentScrollDepths.has(depth)) return false;
  sentScrollDepths.add(depth);
  sentOnce.add(scrollEventNames[depth]);
  return track(scrollEventNames[depth]);
}

export function trackScrollProgress(input: { scrollY: number; documentHeight: number; viewportHeight: number }): PlausibleEventName[] {
  const progress = calculateScrollProgress(input);
  const newlySent: PlausibleEventName[] = [];
  for (const threshold of scrollThresholds) {
    if (progress >= threshold && trackScrollDepth(threshold)) {
      newlySent.push(scrollEventNames[threshold]);
    }
  }
  return newlySent;
}

export function calculateScrollProgress(input: { scrollY: number; documentHeight: number; viewportHeight: number }): number {
  const scrollableHeight = input.documentHeight - input.viewportHeight;
  if (scrollableHeight <= 0) return 0;
  return Math.max(0, Math.min(100, (input.scrollY / scrollableHeight) * 100));
}

export function shouldTrackSalesPageScroll(pathname: string): boolean {
  return pathname === '/';
}

export function trackCtaClick(position: CtaPosition) {
  return track('CtaClicked', { position });
}

export function trackCheckoutViewed(): boolean {
  return trackOnce('CheckoutViewed');
}

export function trackCheckoutStarted(): boolean {
  return trackOnce('CheckoutStarted');
}

export function trackCheckoutContactCompleted(): boolean {
  return trackOnce('CheckoutContactCompleted');
}

export function trackPaymentMethodSelected(method: PaymentMethod) {
  return track('PaymentMethodSelected', { method });
}

export function trackPaymentInitiated(method: PaymentMethod) {
  return track('PaymentInitiated', { method });
}

export function trackPaymentFailed(method: PaymentMethod, category: PaymentFailureCategory, presentationMode?: PaymentPresentationMode) {
  const safeCategory = allowedPaymentFailureCategories.includes(category) ? category : 'unknown';
  return track('PaymentFailed', { method, category: safeCategory, ...(presentationMode ? { presentation_mode: presentationMode } : {}) });
}

export function trackPaymentCanceled(method: PaymentMethod) {
  return track('PaymentCanceled', { method });
}

export function categorizeStripePaymentError(error: unknown): PaymentFailureCategory {
  if (!error || typeof error !== 'object') return 'unknown';
  const stripeError = error as { type?: unknown; code?: unknown; decline_code?: unknown };
  const type = typeof stripeError.type === 'string' ? stripeError.type : '';
  const code = typeof stripeError.code === 'string' ? stripeError.code : '';
  const declineCode = typeof stripeError.decline_code === 'string' ? stripeError.decline_code : '';

  if (type === 'validation_error') return 'validation';
  if (type === 'card_error' || declineCode || code.includes('declin')) return 'declined';
  if (type === 'api_connection_error' || type === 'api_error' || code.includes('network')) return 'network';
  if (code.includes('authentication') || code.includes('3ds') || code.includes('confirm')) return 'authentication';
  return 'unknown';
}

export function resetTrackingStateForTests() {
  sentOnce.clear();
  sentScrollDepths.clear();
}

export function getPlausibleAttribution() {
  return {
    entryUrl: sessionStorage.getItem('funnel_entry_url') ?? window.location.href,
    referrer: sessionStorage.getItem('funnel_initial_referrer') ?? document.referrer,
    landingVariant: getTracking().landing_variant ?? null,
  };
}

export function buildPlausibleProps(): PlausibleProps {
  return {};
}

function safeParse(raw: string): Record<string, string> {
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

declare global {
  interface Window {
    plausible?: ((eventName: PlausibleEventName, options?: { props?: PlausibleProps }) => void) & {
      init?: (options?: Record<string, unknown>) => void;
      o?: Record<string, unknown>;
      q?: unknown[];
    };
  }
}

function trackOnce(eventName: PlausibleEventName): boolean {
  if (sentOnce.has(eventName)) return false;
  sentOnce.add(eventName);
  return track(eventName);
}

function sanitizeCurrentEntryUrl() {
  const url = new URL(window.location.href);
  const clean = new URL(window.location.origin);
  clean.pathname = url.pathname || '/';
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
    const value = url.searchParams.get(key);
    if (value) clean.searchParams.set(key, value.slice(0, 120));
  }
  return clean.toString();
}
